import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccountFilterData, User } from '../../_shared/models';
import { isEmptyObject } from '../../_shared/helpers/object.helper';
import { Observable, Subject, Subscription } from 'rxjs';
import { UnifiedTreeItem } from '../../_shared/models/driver.model';
import { AssetAssignmentFilterComponent } from '../../_shared/components/asset-assignment-filter/asset-assignment-filter.component';
import { AccountService, AuthService } from '../../_shared/services';
import { DriverService } from '../../_shared/services/driver.service';
import { buildAccountEntityTree, buildAssetTree } from '../../_shared/helpers/hierarchy.helper';
import { NgbModal, NgbModalOptions, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { VehicleAssignmentService } from './services/vehicle-assignment.service';
import { AssignedDriver, AssignedVehiclesPaginatedRequest, ModalConfig } from './models/vehicle-assignment.model';
import { AccountFilterComponent } from '../../_shared/components';
import { VehicleAssignmentModalComponent } from '../_shared/components/modals/driver-assignment/vehicle-assignment-modal.component';
import { Router, RouterModule } from '@angular/router';
import { SearchComponent } from '../../_shared/components/filters/search/search.component';
import { PaginationComponent } from "../../_shared/components/pagination/pagination.component";
import { DriverVehicleAssignmentRowComponent } from "./components/driver-vehicle-assignment-row/driver-vehicle-assignment-row.component";
import { BulkAssignmentModalConfig, BulkUnAssignmentModalConfig } from '../../_shared/constants/assignment-modal-config.constants';
import { AssignmentFilterService } from './services/assignment-filter.service';
import { ErrorHandlerService } from '../../_shared/services/error-handler.service';
import { UserRole } from '../../_shared/models/user-roles.enum';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-vehicle-assignment',
  standalone: true,
  imports: [
    FormsModule,
    AssetAssignmentFilterComponent,
    AccountFilterComponent,
    RouterModule,
    NgbTooltipModule,
    SearchComponent,
    PaginationComponent,
    DriverVehicleAssignmentRowComponent,
    TranslocoDirective,
    TranslocoPipe
],
  templateUrl: './driver-vehicle-assignment.component.html',
  styleUrl: './driver-vehicle-assignment.component.scss'
})
export class DriverVehicleAssignmentComponent implements OnInit {
  private accountsService: AccountService = inject(AccountService);

  driversData: any[];
  isLoading: boolean = false;
  hasLoaded: boolean = false;
  user: User;
  accountFilterChangedSubject: Subject<number[]> = new Subject<number[]>();
  accountFilterChanged$: Observable<number[]> = this.accountFilterChangedSubject.asObservable();
  assignmentFilter = { drivers: [], assets: [] }
  driversTree: UnifiedTreeItem[] = [];
  hierarchicalDriversTree: UnifiedTreeItem[] = [];
  hierarchicalAssetsTree: UnifiedTreeItem[] = [];
  selectedAccounts: Array<number> = [];
  driverAssignedVehicles: AssignedDriver[] = [];
  hasFleetManagerRole: boolean = false;
  filterSubscription: Subscription;
  totalItems: number = 0;
  currentItems: number = 0;
  numberOfPages: number = 0;
  currentPage: number = 1;
  pageSize: number = 8;
  isBulkAssignment: boolean = true;
  driversLoaded: WritableSignal<boolean> = signal(false);
  assetsLoaded: WritableSignal<boolean> = signal(false);
  accountFilter: AccountFilterData;
  paginatedRequest: AssignedVehiclesPaginatedRequest = {
    accountIds: [],
    page: 1,
    pageSize: this.pageSize,
    searchTerm: ''
  };

  private authService = inject(AuthService);
  private driverService = inject(DriverService);
  private vehicleAssignmentService = inject(VehicleAssignmentService);
  private modalService = inject(NgbModal);
  private router = inject(Router);
  private assignmentFilterService = inject(AssignmentFilterService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  private refreshIntervalId: any;
  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.accountFilter = {} as AccountFilterData;
  }

  async ngOnInit() {
    this.user = this.authService.getUser();
    this.hasFleetManagerRole = this.authService.hasRole(UserRole.GTFleetManager);
    this.assignmentFilterService.resetBillingAccountId();

    this.paginatedRequest.pageSize = this.hasFleetManagerRole ? this.pageSize : 10;
    //this.startRefreshInterval(); //auto refresh not needed for now.
  }

  ngOnDestroy(): void {
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
    }
  }

  startRefreshInterval() {
    const refreshTime = this.authService.getUserSettings()?.dashboardRefreshTime;
    if (!refreshTime) {
      return
    }

    this.refreshIntervalId = setInterval(() => {
      this.onAccountFilterChange(this.accountFilter);
    }, refreshTime * 1000);
  }

  async onAccountFilterChange(filter: AccountFilterData) {
    if (!isEmptyObject(filter)) {
      this.accountFilter = filter;
      this.accountFilterChangedSubject.next(filter.accountIds);
      this.paginatedRequest.accountIds = filter.accountIds;
      this.selectedAccounts = filter.accountIds;

      await this.getAssignedDriversAsync();
      await this.loadFilterData();
    }
  }

  async createPagination(page: number) {
    if (page > this.numberOfPages || page < 1) {
      return;
    }

    this.paginatedRequest.page = page;
    this.currentPage = page;
    await this.getAssignedDriversAsync();
  }

  async getAssignedDriversAsync() {
    try {
      this.cancelSubject.next();
      this.isLoading = true
      const response = await this.vehicleAssignmentService.getDriverAssignments(this.paginatedRequest, this.cancelSubject);
      this.driverAssignedVehicles = (response.data?.results ?? []);

      this.totalItems = response.data?.totalCount ?? 0;
      this.currentItems = Math.min(this.currentPage * this.paginatedRequest.pageSize, this.totalItems);
      this.numberOfPages = response.data?.totalPages ?? 0;
      this.hasLoaded = true;
      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  async onSearchTermUpdate(searchTerm: string) {
    this.paginatedRequest.searchTerm = searchTerm;
    this.paginatedRequest.page = 1;
    this.currentPage = 1;
    await this.getAssignedDriversAsync();
  }

  async getUserDrivers() {
    if (!!this.user) {
      this.hierarchicalDriversTree = [];
      if (this.hasFleetManagerRole) {
        const response = await this.driverService.getUserDrivers(this.selectedAccounts, this.cancelSubject);
        if (response.data) {
          this.driverService.setDrivers(response.data);
          this.hierarchicalDriversTree = buildAccountEntityTree(response.data.map(d => ({
            ...d,
            entityId: d.driverId,
            entityName: `${d.firstName} ${d.lastName}`,
            entityBillingAccountId: d.billingAccountId
          })), 'driver');
        }
      }
      this.driversLoaded.set(true);
    }
  }

  async loadFilterData() {
    try {
      this.cancelSubject.next();
      await Promise.all([
        this.getUserDrivers(),
        this.getUserAssetsAsync(),
      ]);

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  async getUserAssetsAsync() {
    if (!!this.user) {
      if (this.hasFleetManagerRole) {
        const response = await this.accountsService.getUserAccountAssets(this.selectedAccounts, this.cancelSubject);
        if (response.data) {
          this.accountsService.setAssets(response.data);
          this.hierarchicalAssetsTree = buildAssetTree(response.data);
        }
      }
      this.assetsLoaded.set(true);
    }
  }

  showAllVehicles(driver: any) {
    const driverId = driver.driverId;
    const fullName = `${driver.firstName} ${driver.lastName}`;
    const hasFleetManagerRole = this.hasFleetManagerRole;
    const isDriverDeleted = driver.deleted;
    this.router.navigate(['/driver-management/driver-vehicle-assignment-list'], { state: { fullName, driverId, hasFleetManagerRole, isDriverDeleted } });
  }

  onFilterChanged(filter: any) {
    this.assignmentFilter = filter;
  }

  public getNumbers(): number[] {
    return this.numberOfPages > 0 ? Array.from({ length: this.numberOfPages }, (_, i) => i + 1) : [];
  }

  openBulkAssignModal(): void {
    const config = BulkAssignmentModalConfig;
    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.bulkAssign.title'),
      assignmentFilter: this.assignmentFilter
    }

    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', size: 'xl' };
    const modalRef = this.modalService.open(VehicleAssignmentModalComponent, modalOptions);
    modalRef.componentInstance.config = modalConfig;
    modalRef.closed.subscribe(this.refreshList.bind(this));
  }

  openBulkUnassignModal(): void {
    const config = BulkUnAssignmentModalConfig;
    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.bulkUnassign.title'),
      warningMessage: this.transloco.translate('vehicleAssignment.modals.bulkUnassign.warning'),
      assignmentFilter: this.assignmentFilter
    }

    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', size: 'xl' };
    const modalRef = this.modalService.open(VehicleAssignmentModalComponent, modalOptions);

    modalRef.componentInstance.config = modalConfig;
    modalRef.closed.subscribe(this.refreshList.bind(this));
  }

  refreshList() {
    this.assignmentFilter = { drivers: [], assets: [] }
    this.getAssignedDriversAsync();
  }

  get buttonDisabled(): boolean {
    return (this.assignmentFilter.drivers.length === 0) || (this.assignmentFilter.assets.length === 0);
  }

  getDriverName(firstName: string, lastName: string): string {
    return `${firstName} ${lastName}`
  }
}